import { Component, OnInit, Input } from "@angular/core";
import { Validators, FormGroup, FormBuilder } from "@angular/forms";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { NifValidator } from "@edp-validators/nif.validator";
import { CpeCuiValidator } from "@edp-validators/cpe-cui.validator";
import { EdpModalService } from "@edp-components/edp-modal";
import { ContractService } from "@edp-services/contract.service";
import { ApiErrors } from "@edp-enums/api-errors.enum";
import { TranslateService } from "@ngx-translate/core";
import { EdpResponseBody } from "@edp-models/response.model";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { GetPendingContracts } from "@edp-store/actions/contract.actions";
import { Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { AppBannerService } from "@edp-services/app-banner.service";
import { DatalayerService } from "@edp-services/datalayer.service";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { ModalOrigin } from "@edp-models/modal.model";

@Component({
    selector: "edp-contract-third-party",
    templateUrl: "./edp-contract-third-party.component.html",
    styleUrls: ["./edp-contract-third-party.component.scss"],
})
export class EdpContractThirdPartyComponent implements OnInit {
    @Input() public data: IModalData;
    public form: FormGroup;
    public isSubmitted: boolean;
    public isAlreadyManaged: boolean;
    public onSuccess: boolean;
    public loading = false;
    public holderNif: string;
    public holderName: string;
    public showAppBanner = false;
    private args = {
        CPE_CUI: null,
        Nif: null,
        OwnerName: null,
    };
    public showCpecuiError: boolean;
    public showNifError: boolean;

    public eventConfirm: string;
    public eventClose: string;

    public CLICK_SEULOCAL_ADD3OS_CLOSE = "click_seulocal_add3os_close";
    public CLICK_SEULOCAL_ADD3OS_CONFIRM = "click_seulocal_add3os_confirm";

    public CLICK_START_ADD3OS_CLOSE = "click_start_add3os_close";
    public CLICK_START_ADD3OS_CONFIRM = "click_start_add3os_confirm";

    constructor(
        private fb: FormBuilder,
        private contractService: ContractService,
        private edpModalService: EdpModalService,
        private translate: TranslateService,
        private toaster: EdpToasterService,
        private store: Store<IAppState>,
        private readonly appBannerService: AppBannerService,
        private datalayerService: DatalayerService,
        private readonly operationHistoryService: OperationHistoryService
    ) {}

    ngOnInit(): void {
        this.eventConfirm =
            this.data.origin === ModalOrigin.CONTRACT_LOCAL_DEATILS ? this.CLICK_SEULOCAL_ADD3OS_CONFIRM : this.CLICK_START_ADD3OS_CONFIRM;
        this.eventClose =
            this.data.origin === ModalOrigin.CONTRACT_LOCAL_DEATILS ? this.CLICK_SEULOCAL_ADD3OS_CLOSE : this.CLICK_START_ADD3OS_CLOSE;

        this.showAppBanner = this.appBannerService.show();
        this.form = this.fb.group({
            cpecui: ["", [Validators.required, CpeCuiValidator()]],
            nif: ["", [Validators.required, NifValidator()]],
        });
    }

    public submit(): void {
        this.fireOperationHistoryEvent(this.eventConfirm);
        this.isAlreadyManaged = false; // reset

        if (this.form.valid) {
            this.loading = true;
            this.isSubmitted = true;
            this.checkValidControls(); // reset old msgs
            this.checkContract();

            this.datalayerService.setGaClick("newConsumptionPlaceModalSubmit");
        } else {
            this.checkValidControls();
            this.isSubmitted = false;
        }
    }

    public closeModal(action: boolean): void {
        this.fireOperationHistoryEvent(this.eventClose);
        if (action) {
            this.datalayerService.setGaEvent("newConsumptionPlaceModalClose");
            this.addThirdPartyContract();
        } else {
            this.edpModalService.dismiss(false);
        }
    }

    private checkValidControls(): void {
        this.showCpecuiError = this.isInValidAndDirty("cpecui");
        this.showNifError = this.isInValidAndDirty("nif");

        const errorFields = [];
        if (!!this.showCpecuiError && !!this.showNifError) {
            errorFields.push("NIF", "CPE ou CUI");
        } else {
            if (!!this.showCpecuiError) {
                errorFields.push("CPE ou CUI");
            }

            if (!!this.showNifError) {
                errorFields.push("NIF");
            }
        }

        this.datalayerService.setGaClick("newConsumptionPlaceModalError", { serviceAttributes: { campoComErro: errorFields } });
    }

    private isInValidAndDirty(ctrl: string): boolean {
        const elm = this.form.controls[ctrl];
        return elm.invalid && elm.dirty;
    }

    private checkContract(): void {
        this.args = {
            CPE_CUI: this.form.controls.cpecui.value,
            Nif: this.form.controls.nif.value,
            OwnerName: null,
        };

        this.contractService.getContractsToAssociate(this.args).subscribe(
            resp => this.checkResponseStatus(resp as EdpResponseBody),
            (response: Error) => {
                if (response.message === ApiErrors.CONTRACT_ALREADY_MANAGED) {
                    this.isAlreadyManaged = true;
                } else {
                    const errorMessage = `pt.edp.edponline.endpoint.error.${response.message}`;

                    this.toaster.create(this.translate.instant(errorMessage), EdpToastType.error).show();
                }

                this.isSubmitted = false;
                this.loading = false;
            }
        );
    }

    private addThirdPartyContract(): void {
        this.loading = true;
        this.contractService.addContract(this.args).subscribe(
            (resp: EdpResponseBody) => {
                if (resp.Success) {
                    this.toaster
                        .create(this.translate.instant("pt.edp.edponline.contract.3thparty.valid_during_30"), EdpToastType.success)
                        .show();

                    this.store.dispatch(new GetPendingContracts());

                    this.edpModalService.close(false);
                } else {
                    this.toaster.create(this.translate.instant("pt.edp.edponline.endpoint.error"), EdpToastType.error).show();
                }

                this.loading = false;
            },
            (response: Error) => {
                this.toaster.create(response.message, EdpToastType.error).show();
                this.loading = false;
            }
        );
    }

    private checkResponseStatus(resp: EdpResponseBody): void {
        switch (true) {
            case !resp.Result:
                this.setToast("pt.edp.edponline.contract.client_not_found");
                break;
            case !resp.Result.Collection.previousAccessForTodayRequested:
                this.setUserInfo(resp.Result.Collection);
                break;
            case resp.Result.Collection.previousAccessForTodayRequested:
                this.setToast("pt.edp.edponline.contract.order.placed");
                break;
            default:
                break;
        }
        this.isSubmitted = false;
        this.loading = false;
    }

    public fireOperationHistoryEvent(event: string) {
        return this.operationHistoryService.registerWithContract(null, event);
    }

    private setUserInfo(collection) {
        if (collection.name && collection.nif) {
            this.args.OwnerName = this.holderName = collection.name;
            this.holderNif = collection.nif;
            this.onSuccess = true;
        } else {
            this.setToast("pt.edp.edponline.contract.client_not_found");
        }
    }

    private setToast(label: string) {
        this.toaster.create(this.translate.instant(label), EdpToastType.error).show();
    }
}
